"use client"

import { useSyncExternalStore } from "react"

/**
 * Live progress for whichever file DashboardUploadContent's batch engine is actively streaming
 * right now. The engine is a plain async loop (startUpload → uploadFileWithSession), not tied to
 * the component's mount lifecycle — navigating away from the Upload view unmounts the component
 * but does not cancel that loop, so it keeps running (and this store keeps getting updated) even
 * while some other view (e.g. Job History) is on screen. Mirrors the pattern in
 * lib/download-job-store.ts, minus persistence (only ever one entry: the file currently in flight).
 */
export type LiveUploadJob = {
  uploadSessionId: string
  fileName: string
  fileSize: number
  progress: number
  paused: boolean
}

const jobs = new Map<string, LiveUploadJob>()
const listeners = new Set<() => void>()
let snapshot: LiveUploadJob[] = []

function rebuildSnapshot() {
  snapshot = Array.from(jobs.values())
}

function notify() {
  rebuildSnapshot()
  for (const listener of listeners) listener()
}

export function upsertLiveUploadJob(job: LiveUploadJob) {
  jobs.set(job.uploadSessionId, job)
  notify()
}

export function patchLiveUploadJob(uploadSessionId: string, patch: Partial<LiveUploadJob>) {
  const existing = jobs.get(uploadSessionId)
  if (!existing) return
  jobs.set(uploadSessionId, { ...existing, ...patch })
  notify()
}

export function removeLiveUploadJob(uploadSessionId: string) {
  if (!jobs.has(uploadSessionId)) return
  jobs.delete(uploadSessionId)
  notify()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return snapshot
}

export function useLiveUploadJobs(): LiveUploadJob[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/**
 * Pause/resume/stop for the single active upload batch. There's only ever one DashboardUploadContent
 * run in flight at a time, so one slot (rather than per-session controllers) is enough and keeps
 * these semantics identical to the Upload page's own Pause All/Stop All buttons. Registered for the
 * duration of the actual upload (start → complete/fail/stop) rather than the component's mount
 * lifecycle, so it stays callable from Job History after the Upload page has been navigated away
 * from mid-upload.
 */
export type ActiveUploadBatchControls = {
  togglePause: () => void
  stop: () => void
  isPaused: () => boolean
}

let activeBatchControls: ActiveUploadBatchControls | null = null
const controlListeners = new Set<() => void>()

export function setActiveUploadBatchControls(controls: ActiveUploadBatchControls | null) {
  activeBatchControls = controls
  for (const listener of controlListeners) listener()
}

function getActiveUploadBatchControls() {
  return activeBatchControls
}

function subscribeControls(listener: () => void) {
  controlListeners.add(listener)
  return () => controlListeners.delete(listener)
}

export function useActiveUploadBatchControls(): ActiveUploadBatchControls | null {
  return useSyncExternalStore(subscribeControls, getActiveUploadBatchControls, getActiveUploadBatchControls)
}

/**
 * One-shot "something just finished/failed, your cached job list is stale" signal — fired right when
 * a file's session completes (or the batch stops), instead of leaving every mounted useUploadJobs()
 * consumer to find out via its own next poll (up to 15s later, see dashboard-job-detail-content.tsx)
 * or the next time it happens to remount. Plain listener set, not a stateful store: there's nothing
 * to read, just an event to react to.
 */
const changeListeners = new Set<() => void>()

export function notifyUploadJobsChanged() {
  for (const listener of changeListeners) listener()
}

export function subscribeUploadJobsChanged(listener: () => void): () => void {
  changeListeners.add(listener)
  return () => changeListeners.delete(listener)
}
